import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import SEOHead from '../components/common/SEOHead';

const {FiServer,FiShield,FiClock,FiUsers,FiCheck,FiArrowRight,FiStar,FiTrendingUp}=FiIcons;

const Home=()=> {
  const benefits=[ 
    {icon: FiServer,title: '99.9% uptime guarantee',description: 'Reliable hosting infrastructure ensuring your website stays online when your customers need it most.'},{icon: FiTrendingUp,title: 'Lightning-fast performance',description: 'Optimised servers and advanced caching deliver exceptional loading speeds for better user experience.'},{icon: FiClock,title: '24/7 expert support',description: 'Round-the-clock technical assistance from experienced professionals who understand your business needs.'},{icon: FiUsers,title: 'Local South African expertise',description: 'Based in Waterfall,Midrand,we understand the unique requirements of South African businesses.'} 
  ];

  const testimonials=[ 
    {name: 'Tebo Oliphant',company: 'Tebo Oliphant & Associates',content: 'Primehost transformed our online presence with exceptional hosting reliability and professional web design. Our website performance improved dramatically.',rating: 5},{name: 'David Thompson',company: 'Thompson Consulting',content: 'Outstanding technical support and local expertise. The team at Primehost truly understands South African business requirements.',rating: 5},{name: 'Lisa Van Der Merwe',company: 'Creative Solutions',content: 'Professional service from start to finish. Our new website design perfectly captures our brand and the hosting is incredibly reliable.',rating: 5} 
  ];

  const stats=[ 
    {number: '10+',label: 'Years of expertise'},{number: '99.9%',label: 'Uptime guarantee'},{number: '24/7',label: 'Technical support'},{number: '500+',label: 'Satisfied clients'} 
  ];

  return ( 
    <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="pt-20" > 
      <SEOHead 
        title="Primehost - Premium Web Hosting & Design Solutions | South Africa"
        description="Professional web hosting, email hosting, and web design services in South Africa. 99.9% uptime guarantee, 24/7 support, and local expertise in Waterfall, Midrand."
        keywords="web hosting, email hosting, web design, domain registration, South Africa, Midrand, Waterfall, hosting services, website design, premium hosting"
      />
      
      {/* Hero Section */} 
      <section className="relative bg-gradient-to-br from-gray-50 to-white py-20 lg:py-32 overflow-hidden"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"> 
            <motion.div initial={{opacity: 0,x: -50}} animate={{opacity: 1,x: 0}} transition={{duration: 0.6}} > 
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6"> Elevate your business with{' '} <span className="text-primary-500">digital excellence</span> </h1> 
              <p className="text-xl text-gray-600 mb-8 leading-relaxed"> Combine striking design with rock-solid technical performance. Professional web hosting,email solutions,and custom web design for South African businesses seeking reliable digital infrastructure. </p> 
              <div className="flex flex-col sm:flex-row gap-4 mb-8"> 
                <Link to="/contact" className="bg-primary-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-600 transition-colors duration-200 text-center" > Get started today </Link> 
                <Link to="/services" className="border border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:border-primary-500 hover:text-primary-500 transition-colors duration-200 text-center" > Explore services </Link> 
              </div> 
            </motion.div> 
            <motion.div initial={{opacity: 0,x: 50}} animate={{opacity: 1,x: 0}} transition={{duration: 0.6,delay: 0.2}} className="relative" > 
              <img src="https://media.phst.co.za/file/ph/primehost-home-hero.jpg" alt="Professional web development and hosting" className="rounded-2xl shadow-2xl" /> 
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg"> 
                <div className="flex items-center space-x-3"> 
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div> 
                  <span className="text-sm font-medium text-gray-900">99.9% uptime guaranteed</span> 
                </div> 
              </div> 
            </motion.div> 
          </div> 
        </div> 
      </section> 

      {/* Trust Indicators */} 
      <section className="py-16 bg-gray-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <motion.div initial={{opacity: 0,y: 20}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.6}} viewport={{once: true}} className="grid grid-cols-2 lg:grid-cols-4 gap-8" > 
            {stats.map((stat,index)=> ( 
              <div key={index} className="text-center"> 
                <div className="text-3xl lg:text-4xl font-bold text-primary-500 mb-2"> {stat.number} </div> 
                <div className="text-gray-600 font-medium"> {stat.label} </div> 
              </div> 
            ))} 
          </motion.div> 
        </div> 
      </section> 

      {/* Key Benefits */} 
      <section className="py-20 bg-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <motion.div initial={{opacity: 0,y: 20}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.6}} viewport={{once: true}} className="text-center mb-16" > 
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4"> Why choose Primehost for your digital infrastructure? </h2> 
            <p className="text-xl text-gray-600 max-w-3xl mx-auto"> We combine technical excellence with local expertise to deliver reliable,high-performance solutions that grow with your business. </p> 
          </motion.div> 
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8"> 
            {benefits.map((benefit,index)=> ( 
              <motion.div key={index} initial={{opacity: 0,y: 20}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.6,delay: index * 0.1}} viewport={{once: true}} className="bg-gray-50 p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow duration-300" > 
                <div className="flex items-start space-x-4"> 
                  <div className="bg-primary-100 p-3 rounded-xl"> 
                    <SafeIcon icon={benefit.icon} className="w-6 h-6 text-primary-500" /> 
                  </div> 
                  <div> 
                    <h3 className="text-xl font-semibold text-gray-900 mb-2"> {benefit.title} </h3> 
                    <p className="text-gray-600 leading-relaxed"> {benefit.description} </p> 
                  </div> 
                </div> 
              </motion.div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* Value Proposition */} 
      <section className="py-20 bg-gray-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"> 
            <motion.div initial={{opacity: 0,x: -50}} whileInView={{opacity: 1,x: 0}} transition={{duration: 0.6}} viewport={{once: true}} > 
              <img src="https://media.phst.co.za/file/ph/primehost-home-solutions.jpg" alt="Team collaboration and technical expertise" className="rounded-2xl shadow-xl" /> 
            </motion.div> 
            <motion.div initial={{opacity: 0,x: 50}} whileInView={{opacity: 1,x: 0}} transition={{duration: 0.6,delay: 0.2}} viewport={{once: true}} > 
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6"> Comprehensive digital solutions tailored for South African businesses </h2> 
              <p className="text-lg text-gray-600 mb-8"> From reliable web hosting to stunning website design,we provide integrated solutions that combine IT knowledge with business acumen to drive your success. </p> 
              <div className="space-y-4 mb-8"> 
                {[ 'Enterprise-grade security and performance','Local expertise with global standards','Scalable solutions that grow with your business','Comprehensive support and maintenance' ].map((feature,index)=> ( 
                  <div key={index} className="flex items-center space-x-3"> 
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500" /> 
                    <span className="text-gray-700">{feature}</span> 
                  </div> 
                ))} 
              </div> 
              <Link to="/about" className="inline-flex items-center space-x-2 text-primary-500 font-semibold hover:text-primary-600 transition-colors duration-200" > 
                <span>Learn more about us</span> 
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" /> 
              </Link> 
            </motion.div> 
          </div> 
        </div> 
      </section> 

      {/* Social Proof */} 
      <section className="py-20 bg-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <motion.div initial={{opacity: 0,y: 20}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.6}} viewport={{once: true}} className="text-center mb-16" > 
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4"> Trusted by South African businesses </h2> 
            <p className="text-xl text-gray-600"> See what our clients say about their experience with Primehost </p> 
          </motion.div> 
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8"> 
            {testimonials.map((testimonial,index)=> ( 
              <motion.div key={index} initial={{opacity: 0,y: 20}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.6,delay: index * 0.1}} viewport={{once: true}} className="bg-gray-50 p-8 rounded-2xl shadow-sm" > 
                <div className="flex space-x-1 mb-4"> 
                  {[...Array(testimonial.rating)].map((_,i)=> ( 
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" /> 
                  ))} 
                </div> 
                <p className="text-gray-700 mb-6 leading-relaxed"> "{testimonial.content}" </p> 
                <div> 
                  <div className="font-semibold text-gray-900">{testimonial.name}</div> 
                  <div className="text-sm text-gray-600">{testimonial.company}</div> 
                </div> 
              </motion.div> 
            ))} 
          </div> 
          <motion.div initial={{opacity: 0,y: 20}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.6,delay: 0.4}} viewport={{once: true}} className="text-center mt-12" > 
            <div className="bg-primary-50 rounded-2xl p-8"> 
              <h3 className="text-2xl font-bold text-gray-900 mb-4"> Performance improvements our clients experience </h3> 
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6"> 
                <div className="text-center"> 
                  <div className="text-3xl font-bold text-primary-500 mb-2">35%</div> 
                  <div className="text-gray-600">Increase in page views</div> 
                </div> 
                <div className="text-center"> 
                  <div className="text-3xl font-bold text-primary-500 mb-2">28%</div> 
                  <div className="text-gray-600">Reduction in bounce rates</div> 
                </div> 
              </div> 
            </div> 
          </motion.div> 
        </div> 
      </section> 

      {/* CTA Section */} 
      <section className="py-20" style={{backgroundColor: '#D14932'}}> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"> 
          <motion.div initial={{opacity: 0,y: 20}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.6}} viewport={{once: true}} > 
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6"> Ready to elevate your digital presence? </h2> 
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto"> Join hundreds of South African businesses who trust Primehost for their digital infrastructure. Get started with professional hosting and design solutions today. </p> 
            <div className="flex flex-col sm:flex-row gap-4 justify-center"> 
              <Link to="/contact" className="bg-white text-red-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200" > Get started today </Link> 
              <Link to="/pricing" className="border border-red-300 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-600 transition-colors duration-200" > View pricing </Link> 
            </div> 
          </motion.div> 
        </div> 
      </section> 
    </motion.div> 
  );
};

export default Home;